import { useState, useMemo } from 'react';
import { KPICard } from '@/components/dashboard/KPICard';
import { LiveCaseFeed } from '@/components/dashboard/LiveCaseFeed';
import { ROICalculatorModal } from '@/components/dashboard/ROICalculatorModal';
import { LiveClaimsPulse } from '@/components/dashboard/LiveClaimsPulse';
import { CashVelocityChart } from '@/components/dashboard/CashVelocityChart';
import { DenialPreventionChart } from '@/components/dashboard/DenialPreventionChart';
import { PayerDriftRadar } from '@/components/dashboard/PayerDriftRadar';
import { ClinicalAuthorityCard } from '@/components/dashboard/ClinicalAuthorityCard';
import { CincinnatiAdvantageCard } from '@/components/dashboard/CincinnatiAdvantageCard';
import { useClaimsStream } from '@/hooks/useClaimsStream';
import { 
  generateKPIData, 
  generateCashVelocityData, 
  generateDenialPreventionData,
  generatePayerDriftData 
} from '@/lib/mockData';
import { 
  Shield, 
  Clock, 
  AlertTriangle 
} from 'lucide-react';

export default function Dashboard() {
  const [isPaused, setIsPaused] = useState(false);
  const { claims } = useClaimsStream([], isPaused);
  
  // Generate mock data (in real app, this would come from API)
  const kpiData = useMemo(() => generateKPIData(), []);
  const cashVelocityData = useMemo(() => generateCashVelocityData(), []);
  const denialPreventionData = useMemo(() => generateDenialPreventionData(), []);
  const payerDriftData = useMemo(() => generatePayerDriftData(), []);

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight">Command Center</h1>
          <p className="text-xs md:text-sm text-muted-foreground">
            Revenue Integrity • Real-time Monitoring
          </p>
        </div>
        <div className="flex items-center gap-3 self-start sm:self-auto">
          <ROICalculatorModal />
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-success/10 border border-success/20">
            <div className="pulse-dot bg-success" />
            <span className="text-xs font-medium text-success">All Systems Operational</span>
          </div>
        </div>
      </div>

      {/* Revenue Integrity KPI Strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        <KPICard
          title="Authorized Revenue at Risk"
          value={245600}
          delta={-4.2}
          format="currency"
          icon={<AlertTriangle className="w-4 h-4" />}
        />
        <KPICard
          title="Triple-Match Rate"
          value={94.2}
          delta={2.1}
          format="percentage"
          icon={<Shield className="w-4 h-4" />}
        />
        <KPICard
          title="Pending Surgeon Actions"
          value={14}
          delta={-8}
          deltaLabel="vs yesterday"
          invertDelta
          icon={<Clock className="w-4 h-4" />}
        />
      </div>

      {/* Live Case Feed — Center of Dashboard */}
      <LiveCaseFeed />

      {/* Main Grid - Responsive */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
        <div className="lg:col-span-7">
          <LiveClaimsPulse 
            claims={claims} 
            isPaused={isPaused}
            onTogglePause={() => setIsPaused(!isPaused)}
          />
        </div>
        <div className="lg:col-span-5 space-y-4 md:space-y-6">
          <CincinnatiAdvantageCard />
          <ClinicalAuthorityCard />
          <PayerDriftRadar data={payerDriftData} />
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <CashVelocityChart data={cashVelocityData} />
        <DenialPreventionChart data={denialPreventionData} />
      </div>

      {/* Security Footer */}
      <div className="border-t border-border pt-3">
        <p className="text-[10px] font-mono text-muted-foreground/50 text-center">
          Immutable Audit Trail ID: 0x7f3a9c2e1b4d08f6 | HIPAA Hardened Environment
        </p>
      </div>
    </div>
  );
}
